import {
  IconActivity,
  IconAt,
  IconBell,
  IconBellRing,
  IconBolt,
  IconBot,
  IconBraces,
  IconCard,
  IconCircleCheck,
  IconClock,
  IconCopy,
  IconCpu,
  IconDigest,
  IconDownload,
  IconGitHub,
  IconHeart,
  IconHome,
  IconKey,
  IconLayers,
  IconLayoutDashboard,
  IconLock,
  IconMail,
  IconMessage,
  IconOctagonX,
  IconOrg,
  IconPlug,
  IconRepo,
  IconRocket,
  IconServer,
  IconShield,
  type IconSvgProps,
  IconTerminal,
  IconUsers,
  IconWebhook,
} from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { ADMIN_RAIL, ORG_RAIL, type RailItem } from '../lib/rail';

// The collapsed rail's icons (#7710): one glyph per rail row, keyed by the row's
// route key. The expanded rail stays text-only (the canvas spec); a row's icon
// shows only in the collapsed icon rail, where it is the row's whole face.
//
// `<RailIcon routeKey/>` paints a row's glyph; `RAIL_ICONS` is the table behind it.
// Named for the component (`scripts/file-naming.test.ts`: `components/` is
// PascalCase); its test keeps the issue's name, `rail-icons.test.ts`.
//
// Here and not in `lib/rail.ts`, which stays pure (no components). Checked at
// import, as `assertRailCovers` checks the rail itself, so a drift throws in every
// test that loads the shell rather than shipping a blank square:
//   - a row of `ORG_RAIL` or `ADMIN_RAIL` with no icon throws, naming its key;
//   - two rows of ONE rail sharing an icon throw, naming both, because two
//     identical squares cannot be told apart. Sharing ACROSS the org and admin
//     rails is fine: they never render together.

export type RailGlyph = (props: IconSvgProps) => JSX.Element;

export const RAIL_ICONS: Readonly<Record<string, RailGlyph>> = {
  // Org rail.
  home: IconHome,
  repos: IconRepo,
  dashboards: IconLayoutDashboard,
  digests: IconDigest,
  agents: IconBot,
  terminal: IconTerminal,
  fleet: IconServer,
  plans: IconLayers,
  ci: IconCircleCheck,
  artifacts: IconDownload,
  pending: IconClock,
  blockers: IconOctagonX,
  audit: IconActivity,
  account_keys: IconKey,
  notifications_settings: IconBell,
  billing: IconCard,
  account_members: IconUsers,
  account_squads: IconCpu,
  account_sso: IconShield,
  my_keys: IconLock,
  github_token: IconGitHub,
  logins: IconAt,
  account_tokens: IconBolt,
  system_prompts: IconMessage,
  account_templates: IconCopy,
  account_prompt_vars: IconBraces,
  account_webhooks: IconWebhook,
  email_routes: IconMail,
  integrations: IconPlug,
  donations: IconHeart,
  // Admin rail.
  admin_overview: IconActivity,
  admin_access_requests: IconBellRing,
  admin_users: IconUsers,
  admin_invitations: IconMail,
  admin_grants: IconKey,
  admin_orgs: IconOrg,
  admin_oss: IconRepo,
  admin_goals: IconRocket,
};

/** Every row of one rail has an icon of its own; throws naming the offending keys. */
export function assertRailIcons(
  rows: readonly RailItem[],
  icons: Readonly<Record<string, RailGlyph>>,
): void {
  const seen = new Map<RailGlyph, string>();
  for (const row of rows) {
    const icon = icons[row.key];
    if (icon === undefined) {
      throw new Error(
        `rail-icons: rail row '${row.key}' has no icon — add it to RAIL_ICONS (components/RailIcon.tsx)`,
      );
    }
    const other = seen.get(icon);
    if (other !== undefined) {
      throw new Error(
        `rail-icons: rows '${other}' and '${row.key}' share one icon — give one of them its own glyph`,
      );
    }
    seen.set(icon, row.key);
  }
}

assertRailIcons(
  ORG_RAIL.flatMap((group) => group.items),
  RAIL_ICONS,
);
assertRailIcons(
  ADMIN_RAIL.flatMap((group) => group.items),
  RAIL_ICONS,
);

/** The row's icon, or `undefined` for a key no rail lists (a test's made-up row). */
export function railIcon(key: string): RailGlyph | undefined {
  return RAIL_ICONS[key];
}

/** A rail row's icon at `size`, or nothing for a key no rail lists. Decorative: the caller hides it from AT. */
export function RailIcon(props: { readonly routeKey: string; readonly size: string }): JSX.Element {
  return (
    <Show when={railIcon(props.routeKey)}>
      {(glyph) => <Dynamic component={glyph()} size={props.size} />}
    </Show>
  );
}
